import type { SalesHierarchyModel } from "@/models/pipeline";
import type { Department, Worker } from "@/types/appData";
import type { MultiSelectGroup, MultiSelectOption } from "@/components/ui/multi-select";
import { naturalSort } from "@/helpers/sorting";

/** Team/manager info resolved for a given agent, via their department. */
export interface AgentTeamInfo {
  departmentId: string;
  departmentName: string;
  /** "Team Manager" level. */
  osmId: number;
  osmName: string;
  /** "Head of Sales" level. */
  hosId: number;
  hosName: string;
}

/** A selectable Team option, deduplicated from the sales hierarchy. */
export interface TeamOption {
  id: string;
  name: string;
  osmId: number;
  osmName: string;
  hosId: number;
  hosName: string;
}

const normalizeName = (name: string | null | undefined): string => (name ?? "").trim().toLowerCase();

/**
 * Builds a lookup from normalized agent name -> their team/manager info.
 *
 * Recent Activity rows only carry an `agentName` string, so this is the bridge:
 * agentName -> workers[].name -> departmentId -> salesHierarchy row -> OSM/HoS.
 *
 * If two workers share the same normalized name but sit in different departments, the name is
 * ambiguous and is mapped to `null` rather than guessing — callers should treat this as
 * "Unassigned" instead of attributing the activity to the wrong team.
 */
export function buildAgentTeamIndex(
  workers: Worker[],
  salesHierarchy: SalesHierarchyModel[]
): Map<string, AgentTeamInfo | null> {
  const hierarchyByDepartmentId = new Map<string, SalesHierarchyModel>();
  for (const row of salesHierarchy) {
    if (row?.departmentId && !hierarchyByDepartmentId.has(row.departmentId)) {
      hierarchyByDepartmentId.set(row.departmentId, row);
    }
  }

  const index = new Map<string, AgentTeamInfo | null>();
  const seenDepartmentByName = new Map<string, string>();
  const ambiguousNames = new Set<string>();

  for (const worker of workers) {
    if (!worker?.name || !worker.departmentId) continue;
    const key = normalizeName(worker.name);
    if (!key) continue;

    if (ambiguousNames.has(key)) continue;

    const priorDepartmentId = seenDepartmentByName.get(key);
    if (priorDepartmentId !== undefined && priorDepartmentId !== worker.departmentId) {
      // Same name maps to more than one department — ambiguous, don't guess.
      // Once flagged, keep it ambiguous permanently so a later match on the first
      // department can't overwrite the `null` marker.
      ambiguousNames.add(key);
      index.set(key, null);
      continue;
    }
    seenDepartmentByName.set(key, worker.departmentId);

    const hierarchyRow = hierarchyByDepartmentId.get(worker.departmentId);
    if (!hierarchyRow) {
      index.set(key, null);
      continue;
    }

    index.set(key, {
      departmentId: hierarchyRow.departmentId,
      departmentName: hierarchyRow.departmentName?.trim() || worker.department || hierarchyRow.departmentId,
      osmId: hierarchyRow.osmId,
      osmName: hierarchyRow.osmName,
      hosId: hierarchyRow.hosId,
      hosName: hierarchyRow.hosName,
    });
  }

  return index;
}

/** Safe lookup for a given (possibly null/undefined) agent name. */
export function resolveTeamForAgentName(
  index: Map<string, AgentTeamInfo | null>,
  agentName: string | null | undefined
): AgentTeamInfo | null {
  const key = normalizeName(agentName);
  if (!key) return null;
  return index.get(key) ?? null;
}

/** Dedupes sales-hierarchy rows into selectable Team options, one per department. */
export function buildTeamOptions(
  salesHierarchy: SalesHierarchyModel[],
  departments: Department[] = []
): TeamOption[] {
  const departmentNameById = new Map<string, string>();
  for (const dept of departments) {
    if (dept?.departmentId != null && dept?.department != null) {
      departmentNameById.set(dept.departmentId, dept.department);
    }
  }

  const byId = new Map<string, TeamOption>();
  for (const row of salesHierarchy) {
    if (!row?.departmentId || byId.has(row.departmentId)) continue;
    byId.set(row.departmentId, {
      id: row.departmentId,
      name: row.departmentName?.trim() || departmentNameById.get(row.departmentId) || row.departmentId,
      osmId: row.osmId,
      osmName: row.osmName,
      hosId: row.hosId,
      hosName: row.hosName,
    });
  }

  return Array.from(byId.values()).sort((a, b) => naturalSort(a.name, b.name));
}

/** Groups Team options by their Head of Sales, for a grouped MultiSelect dropdown. */
export function groupTeamOptionsByHoS(teamOptions: TeamOption[]): MultiSelectGroup[] {
  const byHos = new Map<string, { hosName: string; options: MultiSelectOption[] }>();

  for (const team of teamOptions) {
    const key = String(team.hosId);
    if (!byHos.has(key)) {
      byHos.set(key, { hosName: team.hosName || "Unknown", options: [] });
    }
    byHos.get(key)!.options.push({ label: team.name, value: team.id });
  }

  return Array.from(byHos.entries())
    .sort((a, b) => naturalSort(a[1].hosName, b[1].hosName))
    .map(([hosId, group]) => ({ id: hosId, heading: group.hosName, options: group.options }));
}

/** Team ids belonging to the given Head of Sales (by name, case-insensitive). */
export function teamIdsForHeadOfSales(teamOptions: TeamOption[], hosName: string): string[] {
  const key = normalizeName(hosName);
  return teamOptions.filter((t) => normalizeName(t.hosName) === key).map((t) => t.id);
}

/** Team ids belonging to the given Team Manager / OSM (by name, case-insensitive). */
export function teamIdsForTeamManager(teamOptions: TeamOption[], osmName: string): string[] {
  const key = normalizeName(osmName);
  return teamOptions.filter((t) => normalizeName(t.osmName) === key).map((t) => t.id);
}
